import { useEffect, useMemo, useState } from "react";
import type { TimezoneCatalogEntry } from "../utils/timezoneCatalog";
import { formatTimezoneLabel } from "../utils/timezoneDisplay";
import { Modal } from "./Modal";
import { Button } from "./Button";

export function TimezoneCatalogModal({
  open,
  date,
  onClose,
  onSelect,
}: {
  open: boolean;
  date: string;
  onClose: () => void;
  onSelect: (timezoneId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [catalog, setCatalog] = useState<TimezoneCatalogEntry[] | null>(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setQuery("");
    setCatalog(null);
    void import("../utils/timezoneCatalog").then(({ buildTimezoneCatalog }) => {
      if (active) setCatalog(buildTimezoneCatalog(date));
    });
    return () => {
      active = false;
    };
  }, [date, open]);

  const results = useMemo(() => {
    if (!catalog) return [];
    const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
    return terms.length
      ? catalog.filter((entry) =>
          terms.every((term) => entry.searchText.includes(term)),
        )
      : catalog;
  }, [catalog, query]);

  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add timezone"
      description="Search the full timezone catalogue. Offsets are calculated for the edited date."
      icon="travel_explore"
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={() => onSelect(browserZone)}
          className="flex w-full items-center gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-left transition-colors hover:bg-sky-100"
        >
          <span
            className="material-symbols-outlined text-xl text-info"
            aria-hidden
          >
            my_location
          </span>
          <span className="min-w-0">
            <span className="block text-2xs font-extrabold uppercase tracking-wider text-blue-700">
              Browser suggestion
            </span>
            <span className="block truncate text-sm font-bold text-blue-950">
              {formatTimezoneLabel(browserZone, date)}
            </span>
          </span>
        </button>

        <div>
          <label className="form-label" htmlFor="timezone-catalog-search">
            Search city, region, country, IANA ID, abbreviation, or offset
          </label>
          <div className="relative">
            <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-tertiary">
              search
            </span>
            <input
              id="timezone-catalog-search"
              data-autofocus=""
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Try London, Japan, UTC+09:00…"
              className="input-base pl-10"
            />
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto rounded-xl border border-border bg-surface custom-scrollbar">
          {!catalog ? (
            <p
              role="status"
              className="m-0 px-4 py-8 text-center text-sm font-semibold text-secondary"
            >
              Loading timezone catalogue…
            </p>
          ) : results.length ? (
            <ul className="m-0 list-none divide-y divide-border p-0">
              {results.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(entry.id)}
                    className="group flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-subtle focus-visible:bg-subtle"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold text-primary">
                        {entry.label}
                      </span>
                      <span className="block truncate text-xs font-medium text-tertiary">
                        {entry.id}
                      </span>
                    </span>
                    <span
                      className="material-symbols-outlined text-lg text-tertiary group-hover:text-info"
                      aria-hidden
                    >
                      add_circle
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p
              role="status"
              className="m-0 px-4 py-8 text-center text-sm font-semibold text-secondary"
            >
              No timezones match “{query}”.
            </p>
          )}
        </div>
        <p className="m-0 text-xs font-medium text-tertiary" aria-live="polite">
          {catalog
            ? `${results.length} timezone${results.length === 1 ? "" : "s"}`
            : ""}
        </p>
      </div>
    </Modal>
  );
}
